import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { useDelayedFlag } from '@/hooks/useDelayedFlag';

/** 표시 지연 기본값. 이보다 빨리 끝나는 요청에는 스피너를 아예 띄우지 않는다 */
const APPEAR_DELAY = 180;
/** 오버레이 퇴장 시간. index.css 의 lc-fade-out(0.18s) 과 맞춘다 */
const OVERLAY_EXIT_DURATION = 190;

type SpinnerSize = 'sm' | 'md' | 'lg';
type SpinnerColor = 'primary' | 'gray' | 'white' | 'current';

const spinnerSizes: Record<SpinnerSize, string> = {
  sm: 'w-4 h-4',
  md: 'w-6 h-6',
  lg: 'w-8 h-8',
};

const spinnerColors: Record<SpinnerColor, string> = {
  primary: 'text-primary-600',
  gray: 'text-gray-400',
  white: 'text-white',
  // 부모 글자색을 그대로 물려받는다. 버튼 안처럼 색이 상황따라 바뀌는 곳에서 쓴다
  current: '',
};

interface SpinnerProps {
  size?: SpinnerSize;
  color?: SpinnerColor;
  /** 여백·정렬용. 색은 color 로 지정해야 클래스 우선순위 싸움이 없다 */
  className?: string;
}

/** 버튼·배지 등 좁은 자리에 넣는 최소 단위 스피너 */
export const Spinner = ({ size = 'md', color = 'primary', className }: SpinnerProps) => (
  <Loader2
    className={clsx('animate-spin shrink-0', spinnerSizes[size], spinnerColors[color], className)}
    aria-hidden='true'
  />
);

interface LoadingProps {
  message?: string;
  size?: SpinnerSize;
  /** 화면 전체를 덮는 형태로 띄운다. 라우트 전체가 바뀌는 수준의 대기에만 쓸 것 */
  fullscreen?: boolean;
  /** 표시 지연(ms). 0 이면 즉시 노출 */
  delay?: number;
  className?: string;
}

/**
 * 페이지 단위 로딩 표시. 인자 없이 `<Loading />` 으로 호출하면 기존과 같은 모양이다.
 */
export const Loading = ({
  message = '로딩 중...',
  size = 'lg',
  fullscreen = false,
  delay = APPEAR_DELAY,
  className,
}: LoadingProps) => {
  const visible = useDelayedFlag(true, delay);

  return (
    <div
      className={clsx(
        'flex items-center justify-center',
        fullscreen ? 'fixed inset-0 z-[var(--z-modal)] bg-white/70 backdrop-blur-[1px]' : 'min-h-[400px]',
        className,
      )}
      role='status'
      aria-live='polite'
      aria-busy='true'
    >
      {/* 지연 중에도 바깥 상자는 그대로 둔다. 스피너가 뒤늦게 떠도 레이아웃이 밀리지 않는다 */}
      {visible && (
        <div className='text-center animate-fade-in'>
          <Spinner size={size} className='mx-auto mb-2' />
          {message && <p className='text-sm text-gray-500'>{message}</p>}
        </div>
      )}
    </div>
  );
};

interface LoadingOverlayProps {
  visible: boolean;
  message?: string;
}

/**
 * 이미 그려진 목록·표 위를 덮어 "갱신 중"임을 알린다. 내용을 스켈레톤으로 되돌리지 않으므로
 * 재조회 때 화면이 통째로 사라지는 깜빡임이 없다. 부모에 `relative` 가 있어야 한다.
 * visible 이 꺼져도 즉시 사라지지 않고 fade-out 을 마친 뒤 언마운트한다.
 */
export const LoadingOverlay = ({ visible, message }: LoadingOverlayProps) => {
  const [mounted, setMounted] = useState(visible);
  const [prevVisible, setPrevVisible] = useState(visible);

  // 켤 때는 렌더 중에 곧바로 올리고, 끌 때만 아래 effect 가 퇴장 시간을 기다린다
  if (visible !== prevVisible) {
    setPrevVisible(visible);
    if (visible) setMounted(true);
  }

  useEffect(() => {
    if (visible || !mounted) return;

    const timer = window.setTimeout(() => setMounted(false), OVERLAY_EXIT_DURATION);
    return () => window.clearTimeout(timer);
  }, [visible, mounted]);

  if (!mounted) return null;

  return (
    <div
      className={clsx(
        'absolute inset-0 z-10 flex items-center justify-center bg-white/65 backdrop-blur-[1px]',
        // 퇴장 중에는 클릭을 가로채지 않게 해서 목록을 곧바로 다시 만질 수 있게 한다
        visible ? 'animate-fade-in' : 'animate-fade-out pointer-events-none',
      )}
      role='status'
      aria-live='polite'
    >
      <div className='flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-gray-200 shadow-sm'>
        <Spinner size='sm' />
        <span className='text-sm text-gray-600'>{message ?? '갱신 중...'}</span>
      </div>
    </div>
  );
};
